// src/App.js
import "./App.css";
import { useState, useEffect, useRef } from "react";
import { auth, db, functions } from "./firebase";
import { httpsCallable } from "firebase/functions";
import {
  onAuthStateChanged,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  GoogleAuthProvider,
  signInWithPopup,
  getAdditionalUserInfo,
} from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  setDoc,
  query,
  where,
  onSnapshot,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import Calendar, { APP_NAME } from "./Calendar";
import Notes from "./Notes";
import TextFriSo from "./TextFriSo";
import { planNotifText } from "./planChanges";
import AuthScreen, { CheckEmail, needsEmailCheck } from "./AuthScreen";
import BrandLogo from "./BrandLogo";
import { sendRsvp } from "./rsvp";
import { formatTime } from "./dateUtils";

// Google provider with Calendar read scope
// prompt: "consent" forces the full consent screen every time so the
// calendar scope is always explicitly granted (not skipped via cached session)
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope("https://www.googleapis.com/auth/calendar.readonly");
// Write access only to the secondary calendar the app creates for synced events (see Calendar.js).
googleProvider.addScope("https://www.googleapis.com/auth/calendar.app.created");
googleProvider.setCustomParameters({ prompt: "consent" });

// Lasting Google Calendar connection (Phase 2, 2026-10-11): "Connect Google Calendar" gets a one-time code from
// Google Identity Services and the googleConnect function swaps it for a refresh token kept server-side. The app
// then asks googleAccessToken for hour-long access tokens as needed — no more signing out when one runs out.
const GOOGLE_CLIENT_ID = "27530491678-ng2q5uhjdiuba5at5604qf56laq4ift7.apps.googleusercontent.com"; // public
const GOOGLE_CAL_SCOPES = "openid email https://www.googleapis.com/auth/calendar.readonly https://www.googleapis.com/auth/calendar.app.created";
const loadGoogleIdentity = () => new Promise((resolve, reject) => {
  if (window.google?.accounts?.oauth2) return resolve();
  const existing = document.getElementById("gis-client");
  const s = existing || Object.assign(document.createElement("script"), { id: "gis-client", src: "https://accounts.google.com/gsi/client", async: true });
  s.addEventListener("load", () => resolve());
  s.addEventListener("error", () => reject(new Error("Couldn't load Google sign-in. Check your connection and try again.")));
  if (!existing) document.head.appendChild(s);
});

// Installed = the App Store / Google Play app (Capacitor) or FriSo opened from a Home Screen icon. Installed gets the
// bottom tab bar on phones; a phone browser gets a side menu (☰) instead. Doesn't change during a visit.
const INSTALLED_APP = (() => {
  try {
    return Boolean(window.Capacitor?.isNativePlatform?.()) || window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
  } catch { return false; }
})();
// Installed, the phone's status bar takes the theme color (index.html's tan), which left a tan strip above the
// white header. Match it to the header's solid color instead (--color-surface, light and dark).
if (INSTALLED_APP) {
  document.querySelectorAll('meta[name="theme-color"]').forEach((m) => {
    m.setAttribute("content", (m.getAttribute("media") || "").includes("dark") ? "#1a2030" : "#ffffff");
  });
}

// Text FriSo (Settings): hidden until the carriers approve FriSo's texting campaign; "?textfriso" shows it for testing
const TEXT_FRISO_SHOWN = new URLSearchParams(window.location.search).has("textfriso");

// One Settings section: just its title until tapped; one section is open at a time
function SettingsSec({ id, title, open, onToggle, children }) {
  const isOpen = open === id;
  return (
    <section className={"settings__sec" + (isOpen ? " settings__sec--open" : "")}>
      <button type="button" className="settings__head" aria-expanded={isOpen} aria-controls={`settings-${id}`}
        onClick={() => onToggle(isOpen ? null : id)}>
        <span>{title}</span>
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
      </button>
      {isOpen && <div id={`settings-${id}`} className="settings__body">{children}</div>}
    </section>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [unconfirmedUser, setUnconfirmedUser] = useState(null); // password account that hasn't confirmed its email
  const [activeTab, setActiveTab] = useState("stats");
  // Dashboard → Notes hand-off: { noteId } opens a note, { noteId: null } starts a new one
  const [notesIntent, setNotesIntent] = useState(null);
  const [calIntent, setCalIntent] = useState(null); // from a notification: { eventId } or { podId }
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [memberLeftNotifs, setMemberLeftNotifs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [showNotifModal, setShowNotifModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNavDrawer, setShowNavDrawer] = useState(false); // phone browser: side menu
  const drawerSwipe = useRef(null);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(null); // which Settings section is expanded (one at a time)
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  // Google access token, in memory only. From the server connection when there is one ("server"), or from a
  // "Continue with Google" sign-in popup ("popup", lasts an hour and then just goes away — no sign-out).
  const [googleAccessToken, setGoogleAccessToken] = useState(null);
  const [googleConnected, setGoogleConnected] = useState(null); // lasting server-side connection: null = checking
  const [googleConnecting, setGoogleConnecting] = useState(false); // Connect approved in Google, server still saving it
  const tokenSourceRef = useRef(null);
  const userMenuRef = useRef(null);
  const mobileUserMenuRef = useRef(null);
  const tokenTimerRef = useRef(null);

  const clearTokenTimer = () => {
    if (tokenTimerRef.current) {
      clearTimeout(tokenTimerRef.current);
      tokenTimerRef.current = null;
    }
  };

  // Use a token until shortly before it ends, then get a fresh one from the server (or drop a popup token)
  const applyGoogleToken = (token, expiresAt, source) => {
    tokenSourceRef.current = source;
    setGoogleAccessToken(token);
    clearTokenTimer();
    const delay = Math.max(30000, expiresAt - Date.now() - 5 * 60000);
    tokenTimerRef.current = setTimeout(() => {
      if (source === "server") refreshGoogleToken();
      else { tokenSourceRef.current = null; setGoogleAccessToken(null); }
    }, delay);
  };
  const dropGoogleToken = () => {
    clearTokenTimer();
    tokenSourceRef.current = null;
    setGoogleAccessToken(null);
  };
  const refreshGoogleToken = async () => {
    try {
      const { data } = await httpsCallable(functions, "googleAccessToken")();
      if (data.connected) {
        setGoogleConnected(true);
        applyGoogleToken(data.accessToken, data.expiresAt, "server");
        return;
      }
      setGoogleConnected(false);
      if (tokenSourceRef.current === "server") dropGoogleToken(); // a popup token can keep working until its hour is up
    } catch (err) {
      console.error("Google token refresh failed", err);
      clearTokenTimer();
      tokenTimerRef.current = setTimeout(refreshGoogleToken, 2 * 60000); // try again shortly
    }
  };

  // Old builds kept the token in localStorage and signed people out when it expired; clear those leftovers
  useEffect(() => {
    localStorage.removeItem("googleAccessToken");
    localStorage.removeItem("googleAccessTokenExpiry");
    return clearTokenTimer;
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (signedIn) => {
      // Until a new password account confirms its email it only sees the "Check your email" screen
      const currentUser = needsEmailCheck(signedIn) ? null : signedIn;
      setUnconfirmedUser(currentUser ? null : signedIn);
      setUser(currentUser);
      if (currentUser) {
        refreshGoogleToken(); // picks up a lasting connection, if this account has one
        loadGoogleIdentity().catch(() => {}); // preload, so "Connect" opens its popup straight from the click
      } else {
        dropGoogleToken();
        setGoogleConnected(null);
      }
    });
    return unsubscribe;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // "Connect Google Calendar": consent once, the server keeps the connection
  const connectGoogle = async () => {
    setShowUserMenu(false);
    try { await loadGoogleIdentity(); } catch (err) { alert(err.message); return; }
    const client = window.google.accounts.oauth2.initCodeClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: GOOGLE_CAL_SCOPES,
      ux_mode: "popup",
      prompt: "consent", // so Google always issues the lasting (refresh) token
      hint: user?.email || undefined,
      callback: async (resp) => {
        if (resp.error || !resp.code) {
          if (resp.error !== "access_denied") alert("Google didn't connect: " + (resp.error_description || resp.error || "unknown error"));
          return;
        }
        setGoogleConnecting(true); // the server can take a few seconds to wake up; say so instead of looking stuck
        try {
          const { data } = await httpsCallable(functions, "googleConnect")({ code: resp.code });
          setGoogleConnected(true);
          applyGoogleToken(data.accessToken, data.expiresAt, "server");
        } catch (err) {
          alert(err.message || "Couldn't connect Google Calendar. Please try again.");
        } finally {
          setGoogleConnecting(false);
        }
      },
      error_callback: (err) => {
        if (err?.type === "popup_failed_to_open") alert("Your browser blocked the Google window. Allow pop-ups for FriSo and try again.");
        else if (err?.type !== "popup_closed") alert("Couldn't open Google: " + (err?.message || err?.type || "unknown error"));
      },
    });
    client.requestCode();
  };

  const disconnectGoogle = async () => {
    setShowUserMenu(false);
    if (!window.confirm("Disconnect Google Calendar? Your Google events stop showing in FriSo until you connect again.\n\nEvents you've shared with a pod stay with it, but stop updating from Google. You can delete them in FriSo anytime.")) return;
    try {
      await httpsCallable(functions, "googleDisconnect")();
    } catch (err) {
      alert("Couldn't disconnect: " + err.message);
      return;
    }
    setGoogleConnected(false);
    dropGoogleToken();
  };

  // Deletes everything private; shared plans and notes stay with their pods, shown as "Unknown user" (see functions)
  const deleteAccount = async () => {
    setShowUserMenu(false);
    if (!window.confirm(
      "Delete your FriSo account? This can't be undone.\n\n" +
      "Your private plans, notes, contacts and settings are deleted, and you leave every pod. " +
      "Plans and notes you shared stay for your pods, shown as from \"Unknown user\". " +
      "Pods you own go to another member."
    )) return;
    try {
      await httpsCallable(functions, "deleteAccount")();
    } catch (err) {
      alert("Couldn't delete your account: " + err.message);
      return;
    }
    dropGoogleToken();
    await auth.signOut().catch(() => {});
  };

  // Returns the error (for the sign-in screen to show), or nothing
  const signInWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      // First time: keep their Google name as the one pod members see (see acceptInvitation)
      const g = getAdditionalUserInfo(result)?.profile;
      const profileRef = doc(db, "profiles", result.user.uid);
      if (g && !(await getDoc(profileRef).catch(() => null))?.exists()) {
        setDoc(profileRef, { firstName: g.given_name || "", lastName: g.family_name || "", email: result.user.email }).catch(() => {});
      }
      const credential = GoogleAuthProvider.credentialFromResult(result);
      // credentialFromResult can return a credential with a null accessToken on first
      // sign-in. The raw OAuth token is always available in _tokenResponse as a fallback.
      const token = credential?.accessToken || result._tokenResponse?.oauthAccessToken;
      // Works for this hour; the server connection (if any) replaces it once the auth listener checks
      if (token && tokenSourceRef.current !== "server") applyGoogleToken(token, Date.now() + 55 * 60000, "popup");
    } catch (err) {
      if (err.code !== "auth/popup-closed-by-user" && err.code !== "auth/cancelled-popup-request") return err;
    }
  };

  // Opens an email draft inviting someone to sign up (pod invites themselves are in-app only).
  const inviteFriendByEmail = () => {
    const subject = encodeURIComponent(`Join me on ${APP_NAME}`);
    const body = encodeURIComponent(
      `Hi!\n\nI'm using ${APP_NAME} to share calendars with friends. Sign up here so I can add you to my pod:\n${window.location.origin}`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    setShowUserMenu(false);
  };

  // Mobile: swipe down on any bottom-sheet modal (.modal-overlay > .modal) to close it. Closing clicks the
  // overlay, so each modal's own overlay onClick does the cleanup. A swipe that starts in scrolled content
  // scrolls it instead. Capture-phase native listeners: overlays stop React touchmove propagation, and
  // preventDefault needs { passive: false }. It closes as soon as the drag passes CLOSE_AT, mid-gesture,
  // so a gentle swipe closes.
  useEffect(() => {
    const CLOSE_AT = 50; // px
    let modal = null, scroller = null, dragging = false, startX = 0, startY = 0, startT = 0, dy = 0;
    const dismiss = (m) => {
      m.dataset.swipeClosing = "1";
      m.style.transition = "transform .2s ease";
      m.style.transform = "translateY(100%)";
      setTimeout(() => {
        m.parentElement?.click();
        setTimeout(() => { if (m.isConnected) { m.style.transform = ""; delete m.dataset.swipeClosing; } }, 50); // in case it didn't close
      }, 180);
    };
    const scrollParent = (el, stop) => {
      for (; el; el = el.parentElement) {
        if (el.scrollHeight > el.clientHeight && /auto|scroll/.test(getComputedStyle(el).overflowY)) return el;
        if (el === stop) return null;
      }
      return null;
    };
    const onStart = (e) => {
      modal = window.innerWidth <= 640 && e.touches.length === 1 ? e.target.closest?.(".modal-overlay > .modal") : null;
      if (!modal || modal.dataset.swipeClosing) { modal = null; return; }
      modal.style.transform = ""; // clear any offset left by a gesture whose end we never saw
      scroller = scrollParent(e.target, modal);
      dragging = false;
      dy = 0;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      startT = Date.now();
    };
    const onMove = (e) => {
      if (!modal) return;
      const dx = e.touches[0].clientX - startX;
      const d = e.touches[0].clientY - startY;
      if (!dragging) {
        if (Math.abs(d) < 6 && Math.abs(dx) < 6) return;
        // Only a mostly-vertical downward swipe with the content at its top starts a drag
        if (d <= 0 || Math.abs(dx) > d || (scroller && scroller.scrollTop > 0)) { modal = null; return; }
        dragging = true;
        modal.style.transition = "none";
      }
      e.preventDefault();
      dy = Math.max(0, d);
      if (dy > CLOSE_AT) { dismiss(modal); modal = null; return; }
      modal.style.transform = `translateY(${dy}px)`;
    };
    const onEnd = () => {
      const m = modal;
      modal = null;
      if (!m || !dragging) return;
      // Released before CLOSE_AT: a quick flick still closes, otherwise snap back
      if (dy > 20 && dy / Math.max(1, Date.now() - startT) > 0.5) { dismiss(m); return; }
      m.style.transition = "transform .2s ease";
      m.style.transform = "";
    };
    // Only touchmove calls preventDefault; passive start/end listeners don't delay scrolling elsewhere
    const opts = { capture: true, passive: true };
    const moveOpts = { capture: true, passive: false };
    document.addEventListener("touchstart", onStart, opts);
    document.addEventListener("touchmove", onMove, moveOpts);
    document.addEventListener("touchend", onEnd, opts);
    document.addEventListener("touchcancel", onEnd, opts);
    return () => {
      document.removeEventListener("touchstart", onStart, opts);
      document.removeEventListener("touchmove", onMove, moveOpts);
      document.removeEventListener("touchend", onEnd, opts);
      document.removeEventListener("touchcancel", onEnd, opts);
    };
  }, []);

  // Subscribe to pending invitations for the logged-in user
  useEffect(() => {
    if (!user) { setPendingInvitations([]); return; }
    const q = query(
      collection(db, "invitations"),
      where("inviteeEmail", "==", user.email)
    );
    const unsub = onSnapshot(q, (snap) => {
      const pending = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((inv) => inv.status === "pending");
      setPendingInvitations(pending);
    });
    return unsub;
  }, [user]);

  // Subscribe to "member left" notifications addressed to this user
  useEffect(() => {
    if (!user) { setMemberLeftNotifs([]); return; }
    const q = query(
      collection(db, "notifications"),
      where("recipientEmail", "==", user.email),
      where("status", "==", "unread")
    );
    const unsub = onSnapshot(q, (snap) => {
      setMemberLeftNotifs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [user]);

  // Subscribe to the current user's contacts for notification name resolution
  useEffect(() => {
    if (!user) { setContacts([]); return; }
    const q = query(collection(db, "contacts"), where("ownerUid", "==", user.uid));
    const unsub = onSnapshot(q, (snap) => {
      setContacts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [user]);

  // Close user menu when clicking outside (checks both desktop sidebar ref and mobile header ref)
  useEffect(() => {
    const handler = (e) => {
      const inDesktop = userMenuRef.current?.contains(e.target);
      const inMobile = mobileUserMenuRef.current?.contains(e.target);
      if (!inDesktop && !inMobile) setShowUserMenu(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const closeSettings = () => {
    setShowSettings(false);
    setSettingsOpen(null);
    setShowPasswordForm(false);
    setPasswordError("");
    setPasswordSuccess(false);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
  };

  // Account menu (sidebar + phone header): Settings, then Log out at the bottom. Everything else is in Settings.
  const accountMenuItems = (
    <>
      <button className="user-dropdown__item" onClick={() => { setShowSettings(true); setShowUserMenu(false); }}>Settings</button>
      <div className="user-dropdown__divider" />
      <button className="user-dropdown__item" onClick={() => auth.signOut()}>Log Out</button>
    </>
  );

  const changePassword = async () => {
    setPasswordError("");
    setPasswordSuccess(false);
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError("Please fill in all fields.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match.");
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters.");
      return;
    }
    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setPasswordSuccess(true);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
        setPasswordError("Current password is incorrect.");
      } else {
        setPasswordError(err.message);
      }
    }
  };

  const totalNotifCount = pendingInvitations.length + memberLeftNotifs.length;

  // Server-side so the new member is added to every event/note shared with the pod (see functions/index.js).
  // Joining runs a Cloud Function (a few seconds when it has to start up), so the buttons say "Joining…" meanwhile:
  // invitations being joined carry `_joining` (Dashboard, Pods and Notifications read it).
  const [joiningIds, setJoiningIds] = useState([]);
  const acceptInvitation = async (inv) => {
    if (joiningIds.includes(inv.id)) return;
    setJoiningIds((ids) => [...ids, inv.id]);
    try {
      await httpsCallable(functions, "acceptInvitation")({ invId: inv.id });
    } catch (err) {
      window.alert("Couldn't accept this invitation: " + err.message);
    } finally {
      setJoiningIds((ids) => ids.filter((id) => id !== inv.id));
    }
  };
  const invitesShown = joiningIds.length ? pendingInvitations.map((inv) => (joiningIds.includes(inv.id) ? { ...inv, _joining: true } : inv)) : pendingInvitations;

  const declineInvitation = async (inv) => {
    await updateDoc(doc(db, "invitations", inv.id), { status: "declined" });
  };

  const dismissNotif = async (notif) => {
    await updateDoc(doc(db, "notifications", notif.id), { status: "read" });
  };

  // Clicking a notification opens what it's about and marks it read. Notifications from before 2026-10-12 carry no
  // eventId/noteId, so those just open the Calendar or Notes tab.
  const openNotif = (notif) => {
    setShowNotifModal(false);
    dismissNotif(notif).catch(() => {});
    if (notif.type === "note_shared") {
      if (notif.noteId) setNotesIntent({ noteId: notif.noteId });
      setActiveTab("notes");
    } else if (notif.type === "event_shared" || notif.type === "event_rsvp" || notif.type === "event_changed") {
      if (notif.eventId) setCalIntent({ eventId: notif.eventId });
      setActiveTab("calendar");
    } else {
      if (notif.podId) setCalIntent({ podId: notif.podId });
      setActiveTab("pods");
    }
  };

  // Going / Can't go on a "New event" card. The card keeps the answer (rsvpAnswer) until it's dismissed.
  const [rsvpNotifBusy, setRsvpNotifBusy] = useState(null);
  const answerNotif = async (notif, answer) => {
    setRsvpNotifBusy(notif.id);
    try {
      await sendRsvp({ eventId: notif.eventId, email: user.email, answer, prev: notif.rsvpAnswer || null,
        hostEmail: notif.actorEmail, title: notif.eventTitle, startDate: notif.startDate });
      await updateDoc(doc(db, "notifications", notif.id), { rsvpAnswer: answer });
    } catch (err) {
      console.error("RSVP failed", err);
      alert(err.code === "not-found" ? "This event isn't available anymore." : "Couldn't save your answer. Please try again.");
    }
    setRsvpNotifBusy(null);
  };

  const dismissAllNotifs = async () => {
    const batch = writeBatch(db);
    memberLeftNotifs.forEach((n) => batch.update(doc(db, "notifications", n.id), { status: "read" }));
    await batch.commit();
  };

  if (!user) {
    if (unconfirmedUser) {
      return <CheckEmail user={unconfirmedUser} onConfirmed={() => {
        setUnconfirmedUser(null);
        setUser(auth.currentUser);
        refreshGoogleToken();
        loadGoogleIdentity().catch(() => {});
      }} />;
    }
    return <AuthScreen onGoogle={signInWithGoogle} />;
  }

  const navItems = [
    {
      id: "stats",
      label: "Dashboard",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
          <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
        </svg>
      ),
    },
    {
      id: "calendar",
      label: "Calendar",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
      ),
    },
    {
      id: "notes",
      label: "Notes",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10,9 9,9 8,9"/>
        </svg>
      ),
    },
    {
      id: "pods",
      label: "Pods",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="9" cy="7" r="3"/><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/>
          <circle cx="17" cy="7" r="3"/><path d="M21 21v-2a4 4 0 0 0-3-3.87"/>
        </svg>
      ),
    },
    {
      id: "contacts",
      label: "Contacts",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
        </svg>
      ),
    },
  ];

  return (
    <div className={`app-shell${INSTALLED_APP ? "" : " app-shell--browser"}`}>

      {/* Mobile-only top header (phone browser: ☰ opens the side menu) */}
      <div className="app-mobile-header">
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {!INSTALLED_APP && (
            <button className="mobile-header-btn mobile-menu-btn" aria-label="Menu" aria-expanded={showNavDrawer} onClick={() => setShowNavDrawer(true)}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="4" y1="7" x2="20" y2="7"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="17" x2="20" y2="17"/></svg>
            </button>
          )}
          <div className="app-mobile-header__logo"><BrandLogo /></div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <button className="mobile-header-btn" onClick={() => setShowNotifModal(true)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
            </svg>
            {totalNotifCount > 0 && <span className="notif-badge">{totalNotifCount}</span>}
          </button>
          <div style={{ position: "relative" }} ref={mobileUserMenuRef}>
            <button className="mobile-header-avatar" onClick={() => setShowUserMenu((v) => !v)}>
              {user.email.charAt(0).toUpperCase()}
            </button>
            {showUserMenu && (
              <div className="user-dropdown user-dropdown--mobile">{accountMenuItems}</div>
            )}
          </div>
        </div>
      </div>

      <div className="app-sidebar">
        <div className="app-sidebar__logo"><BrandLogo /></div>

        <nav className="app-sidebar__nav">
          {navItems.map(({ id, label, icon }) => (
            <button
              key={id}
              className={`sidebar-nav-btn${activeTab === id ? " sidebar-nav-btn--active" : ""}`}
              onClick={() => setActiveTab(id)}
            >
              <span className="sidebar-nav-btn__icon">{icon}</span>
              <span className="sidebar-nav-btn__label">{label}</span>
            </button>
          ))}
        </nav>

        <div className="app-sidebar__footer">
          <button className="sidebar-notif-btn" onClick={() => setShowNotifModal(true)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
            </svg>
            <span>Notifications</span>
            {totalNotifCount > 0 && (
              <span className="notif-badge">{totalNotifCount}</span>
            )}
          </button>

          <div className="sidebar-user-wrap" ref={userMenuRef}>
            <button
              className="sidebar-user-btn"
              onClick={() => setShowUserMenu((v) => !v)}
            >
              <div className="sidebar-user-avatar">
                {user.email.charAt(0).toUpperCase()}
              </div>
              <span className="sidebar-user-email">{user.email}</span>
              <span style={{ fontSize: 10, color: "var(--color-text-muted)", flexShrink: 0 }}>▾</span>
            </button>
            {showUserMenu && (
              <div className="user-dropdown user-dropdown--up">{accountMenuItems}</div>
            )}
          </div>
        </div>
      </div>

      <main className="app-main">
        {activeTab === "notes" ? (
          <Notes intent={notesIntent} onIntentDone={() => setNotesIntent(null)} />
        ) : (
          <Calendar
            view={activeTab}
            googleAccessToken={googleAccessToken}
            googleConnected={googleConnected}
            googleConnecting={googleConnecting}
            onGoogleTokenExpired={() => { if (tokenSourceRef.current === "server") refreshGoogleToken(); else dropGoogleToken(); }}
            onReconnectGoogle={connectGoogle}
            pendingInvitations={invitesShown}
            onAcceptInvitation={acceptInvitation}
            onDeclineInvitation={declineInvitation}
            onNavigate={setActiveTab}
            onOpenNote={(noteId) => { setNotesIntent({ noteId }); setActiveTab("notes"); }}
            eventIntent={calIntent?.eventId ? calIntent : null}
            podIntent={calIntent?.podId ? calIntent : null}
            onIntentDone={() => setCalIntent(null)}
          />
        )}
      </main>

      {/* Phone browser: side menu that slides in from the left (tap outside, a tab, ✕, Esc or a swipe left closes it) */}
      {!INSTALLED_APP && (
        <div className={`nav-drawer-wrap${showNavDrawer ? " nav-drawer-wrap--open" : ""}`} aria-hidden={!showNavDrawer}>
          <div className="nav-drawer-overlay" onClick={() => setShowNavDrawer(false)} />
          <nav
            className="nav-drawer"
            aria-label="Main"
            onKeyDown={(e) => { if (e.key === "Escape") setShowNavDrawer(false); }}
            onTouchStart={(e) => { drawerSwipe.current = e.touches[0].clientX; }}
            onTouchEnd={(e) => {
              if (drawerSwipe.current != null && e.changedTouches[0].clientX - drawerSwipe.current < -60) setShowNavDrawer(false);
              drawerSwipe.current = null;
            }}
          >
            <div className="nav-drawer__head">
              <div className="app-mobile-header__logo"><BrandLogo /></div>
              <button className="mobile-header-btn" aria-label="Close menu" tabIndex={showNavDrawer ? 0 : -1} onClick={() => setShowNavDrawer(false)}>✕</button>
            </div>
            <div className="nav-drawer__list">
              {navItems.map(({ id, label, icon }) => (
                <button
                  key={id}
                  tabIndex={showNavDrawer ? 0 : -1}
                  className={`sidebar-nav-btn${activeTab === id ? " sidebar-nav-btn--active" : ""}`}
                  onClick={() => { setActiveTab(id); setShowNavDrawer(false); }}
                >
                  <span className="sidebar-nav-btn__icon">{icon}</span>
                  <span className="sidebar-nav-btn__label">{label}</span>
                </button>
              ))}
            </div>
            <div className="nav-drawer__foot">
              <span className="nav-drawer__email">{user.email}</span>
              <button className="sidebar-nav-btn" tabIndex={showNavDrawer ? 0 : -1} onClick={() => { setShowNavDrawer(false); setShowSettings(true); }}>Settings</button>
              <button className="sidebar-nav-btn" tabIndex={showNavDrawer ? 0 : -1} onClick={() => auth.signOut()}>Log out</button>
            </div>
          </nav>
        </div>
      )}

      {/* Mobile bottom nav (installed app / Home Screen only; hidden in a phone browser) */}
      <nav className="app-bottom-nav">
        {navItems.map(({ id, label, icon }) => (
          <button
            key={id}
            className={`bottom-nav-btn${activeTab === id ? " bottom-nav-btn--active" : ""}`}
            onClick={() => setActiveTab(id)}
          >
            <span className="bottom-nav-btn__icon">{icon}</span>
            <span className="bottom-nav-btn__label">{label}</span>
          </button>
        ))}
      </nav>

      {/* Settings modal: account, password, Google Calendar, invite, delete account */}
      {showSettings && (
        <div className="modal-overlay" onClick={closeSettings}>
          <div className="modal settings" onClick={(e) => e.stopPropagation()}>
            <div className="modal__header">
              <div className="modal__title">Settings</div>
              <button className="btn btn--ghost btn--sm" onClick={closeSettings}>✕</button>
            </div>

            <div className="modal__content">
              <SettingsSec id="account" title="Account" open={settingsOpen} onToggle={setSettingsOpen}>
                <div className="settings__row"><span className="settings__muted">{user.email}</span></div>
                {user.providerData?.some((p) => p.providerId === "password") && (!showPasswordForm ? (
                  <div className="settings__row">
                    <span>Password</span>
                    <button className="btn btn--secondary btn--sm" onClick={() => setShowPasswordForm(true)}>Change password</button>
                  </div>
                ) : (
                  <div className="settings__pw">
                    <div className="form-group">
                      <label className="form-label">Current password</label>
                      <input className="form-input" type="password" placeholder="••••••••" value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)} autoFocus />
                    </div>
                    <div className="form-group">
                      <label className="form-label">New password</label>
                      <input className="form-input" type="password" placeholder="••••••••" value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Confirm new password</label>
                      <input className="form-input" type="password" placeholder="••••••••" value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && changePassword()} />
                    </div>
                    {passwordError && <div style={{ color: "var(--color-danger)", fontSize: 13, marginBottom: 12 }}>{passwordError}</div>}
                    {passwordSuccess && <div style={{ color: "var(--color-success)", fontSize: 13, marginBottom: 12 }}>Password updated successfully.</div>}
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                      <button className="btn btn--ghost btn--sm" onClick={() => { setShowPasswordForm(false); setPasswordError(""); setPasswordSuccess(false); }}>Cancel</button>
                      <button className="btn btn--primary btn--sm" onClick={changePassword}>Update password</button>
                    </div>
                  </div>
                ))}
              </SettingsSec>

              <SettingsSec id="google" title="Google Calendar" open={settingsOpen} onToggle={setSettingsOpen}>
                <div className="settings__row">
                  <span className="settings__muted">{googleConnected ? "Connected. Your Google events show in FriSo." : "Not connected."}</span>
                  {googleConnected ? (
                    <button className="btn btn--secondary btn--sm" onClick={disconnectGoogle}>Disconnect</button>
                  ) : (
                    <button className="btn btn--primary btn--sm" disabled={googleConnecting} onClick={connectGoogle}>{googleConnecting ? "Connecting…" : "Connect"}</button>
                  )}
                </div>
              </SettingsSec>

              {TEXT_FRISO_SHOWN && (
                <SettingsSec id="text" title="Text FriSo" open={settingsOpen} onToggle={setSettingsOpen}>
                  <TextFriSo uid={user.uid} />
                </SettingsSec>
              )}

              <SettingsSec id="invite" title="Invite friends" open={settingsOpen} onToggle={setSettingsOpen}>
                <div className="settings__row">
                  <span className="settings__muted">Send someone a link to sign up for FriSo.</span>
                  <button className="btn btn--secondary btn--sm" onClick={inviteFriendByEmail}>Invite by email</button>
                </div>
              </SettingsSec>

              <SettingsSec id="delete" title="Delete account" open={settingsOpen} onToggle={setSettingsOpen}>
                <div className="settings__row">
                  <span className="settings__muted">Permanently delete your account and private data.</span>
                  <button className="btn btn--danger btn--sm" onClick={deleteAccount}>Delete account</button>
                </div>
              </SettingsSec>
            </div>
          </div>
        </div>
      )}

      {/* Notifications modal */}
      {showNotifModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowNotifModal(false)}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal__header">
              <div className="modal__title">Notifications</div>
              <div style={{ display: "flex", gap: 8 }}>
                {memberLeftNotifs.length > 1 && (
                  <button className="btn btn--secondary btn--sm" onClick={dismissAllNotifs}>Dismiss all</button>
                )}
                <button className="btn btn--ghost btn--sm" onClick={() => setShowNotifModal(false)}>✕</button>
              </div>
            </div>

            <div className="modal__content">
            {totalNotifCount === 0 ? (
              <div style={{ color: "var(--color-text-muted)", fontSize: 13, padding: "12px 0" }}>
                No new notifications.
              </div>
            ) : (
              <>
                {/* Pod invitations */}
                {invitesShown.map((inv) => (
                  <div key={inv.id} className="invite-card">
                    <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginBottom: 2 }}>Pod invitation</div>
                    <div className="invite-card__pod">{inv.podName}</div>
                    <div className="invite-card__from">Invited by {inv.inviterEmail}</div>
                    <div className="invite-card__actions">
                      <button
                        className="btn btn--primary btn--sm"
                        disabled={inv._joining}
                        onClick={() => acceptInvitation(inv)}
                      >
                        {inv._joining ? "Joining…" : "Accept"}
                      </button>
                      <button
                        className="btn btn--secondary btn--sm"
                        disabled={inv._joining}
                        onClick={() => declineInvitation(inv)}
                      >
                        Decline
                      </button>
                    </div>
                  </div>
                ))}

                {/* General notifications: member-left, event_shared, event_rsvp, event_changed, event_cancelled, note_shared */}
                {memberLeftNotifs.map((notif) => {
                  const c = contacts.find((ct) => ct.email === notif.actorEmail);
                  const actorName = c ? [c.firstName, c.lastName].filter(Boolean).join(" ") || notif.actorEmail : notif.actorEmail;
                  let typeLabel, body;
                  if (notif.type === "event_shared") {
                    typeLabel = "New event";
                    body = `${actorName} shared "${notif.eventTitle}" in ${notif.podName}`;
                  } else if (notif.type === "event_rsvp") {
                    typeLabel = notif.answer === "yes" ? "Going" : "Can't go";
                    body = notif.answer === "yes" ? `${actorName} is going to "${notif.eventTitle}"` : `${actorName} can't make it to "${notif.eventTitle}"`;
                  } else if (notif.type === "event_changed" || notif.type === "event_cancelled") {
                    typeLabel = notif.type === "event_changed" ? "Plan changed" : "Plan cancelled";
                    body = planNotifText(notif, actorName);
                  } else if (notif.type === "note_shared") {
                    typeLabel = "New note";
                    body = `${actorName} shared "${notif.noteTitle}" in ${notif.podName}`;
                  } else {
                    typeLabel = "Pod update";
                    body = `${actorName} has left ${notif.podName}`;
                  }
                  return (
                    <div key={notif.id} className="invite-card notif-card">
                      {/* A cancelled plan is gone, so its card has nothing to open */}
                      {(() => {
                        const gone = notif.type === "event_cancelled";
                        const Tag = gone ? "div" : "button";
                        return (
                          <Tag {...(gone ? { className: "notif-card__open notif-card__open--static" } : { type: "button", className: "notif-card__open", onClick: () => openNotif(notif) })}>
                            <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginBottom: 2 }}>{typeLabel}</div>
                            <div className="invite-card__from">{body}</div>
                            {notif.startDate && (
                              <div className="notif-card__when">
                                {gone ? "Was " : notif.type === "event_changed" ? "Now " : ""}
                                {new Date(notif.startDate + "T00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                                {notif.startTime && ` · ${formatTime(notif.startTime)}`}
                                {notif.type === "event_changed" && notif.changes?.includes("place") && notif.location && ` · ${notif.location}`}
                              </div>
                            )}
                            {!gone && <span className="notif-card__go">{notif.type === "note_shared" ? "Open note" : notif.type.startsWith("event_") ? "Open event" : "Open pod"} ›</span>}
                          </Tag>
                        );
                      })()}
                      {notif.type === "event_shared" && notif.rsvp && notif.eventId && (
                        notif.rsvpAnswer ? (
                          <div className="notif-rsvp-done">
                            <span className={`rsvp-badge rsvp-badge--${notif.rsvpAnswer} rsvp-badge--lg`} aria-hidden="true">{notif.rsvpAnswer === "yes" ? "✓" : "✕"}</span>
                            {notif.rsvpAnswer === "yes" ? "You're going" : "You can't go"}
                            <button type="button" className="auth-linkbtn"
                              onClick={() => updateDoc(doc(db, "notifications", notif.id), { rsvpAnswer: null }).catch(() => {})}>
                              Change
                            </button>
                          </div>
                        ) : (
                          <div className="rsvp-ask__btns notif-rsvp">
                            <button type="button" className="rsvp-btn rsvp-btn--primary" disabled={rsvpNotifBusy === notif.id} onClick={() => answerNotif(notif, "yes")}>
                              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>Going
                            </button>
                            <button type="button" className="rsvp-btn" disabled={rsvpNotifBusy === notif.id} onClick={() => answerNotif(notif, "no")}>
                              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>Can't go
                            </button>
                          </div>
                        )
                      )}
                      <div className="invite-card__actions">
                        <button
                          className="btn btn--secondary btn--sm"
                          onClick={() => dismissNotif(notif)}
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
